import TrackedObject from "./TrackedObject";
import HasID from "./HasID";
import { Prescription } from "./Prescription";
import { DeliveryCode } from "./DeliveryCode";
import { Payment } from "./Payment";
import { Patient, PatientAddress } from "./Patient";
import { AcceptanceRecord } from "@/models/AcceptanceRecord";

export interface Order extends HasID, TrackedObject {
    storeID: number;
    patientID: number;
    status: OrderStatus;
    storeName: string;
    rxRfs: string;
    printerAddress: string;
    overridePriority: boolean;
    saturdayDelivery: boolean;
    overrideAddress: boolean;
    disableOneRate: boolean;
    signatureRequired: boolean;
    hipaaDocsInBox: boolean;
    shipToPrescriber: boolean;
    stripeCustomerId: string;
    shipmentId: number;
    requiresAudit: boolean;
    auditId: number;
    centralFillId: string;
    verified?: Date;
    verifiedBy?: string;
    transferToPharmacyId?: number;
    minTemp?: number;
    maxTemp?: number;

    isUrgent?: boolean;
    isPartialFill?: boolean;
    isCompletionOrder?: boolean;

    prescriptions: Array<Prescription>;
    items: Array<OrderOTCItem>;
    payments: Array<Payment>;
    deliveryCode: DeliveryCode | null;
    patient: Patient;

    acceptanceRecords: AcceptanceRecord[];

    promisedDate?: Date | null;
    cancellationReason?: string | null;
    patientAddressId?: number | null;
    patientAddress?: PatientAddress | null;

    patientPickupPreference?: boolean | null;

    clientRequestId?: string | null;
    saveWarning?: string | null;

    // On-account (AR) checkout election carried to verification.
    arAccountId?: number | null;
    arChargeAmount?: number | null;
    arOverrideApproved?: boolean | null;
    arOverrideReason?: string | null;

    // Net AR amount actually posted to the account ledger for this order (populated on read):
    // positive while charged, zero once fully voided/reversed. This is the AR tender applied to the
    // order; it counts toward the payment total but never changes a prescription's patient pay.
    arPosted?: number | null;
}

export enum OrderStatus {
    Error,
    Created,
    Processing,
    Shipped,
    Canceled,
    PartialPayment,
    ReadyForPickup,
    AwaitingPriorAuthorization,
    ReturnToSender,
    MissingAcceptance
}

/** Statuses a new order can be merged into; the rest either reject the merge or strand the moved fill. */
export const MERGEABLE_TARGET_STATUSES: OrderStatus[] = [
    OrderStatus.Created, OrderStatus.Processing, OrderStatus.PartialPayment, OrderStatus.MissingAcceptance,
];

export const isPromisedApplicable = (orderStatus: number, promisedDate?: Date | null) : boolean => {
    return [1, 2, 5].includes(orderStatus) && !!promisedDate;
}

export class Order {
    constructor(order?: Order) {
        if (order) {
            Object.assign(this, order);
            this.patient = new Patient(order.patient);
            this.prescriptions = order.prescriptions?.map(rx => new Prescription(rx)) ?? new Array<Prescription>();
            this.items = order.items ?? new Array<OrderOTCItem>();
            this.payments = order.payments ?? new Array<Payment>();
            this.acceptanceRecords = order.acceptanceRecords?.map(ar => new AcceptanceRecord(ar)) ?? new Array<AcceptanceRecord>();
            this.promisedDate = this.promisedDate ?? null;
            this.patientAddressId = this.patientAddressId ?? null;
        } else {
            this.patient = new Patient();
            this.prescriptions = new Array<Prescription>();
            this.items = new Array<OrderOTCItem>();
            this.payments = new Array<Payment>();
            this.acceptanceRecords = new Array<AcceptanceRecord>();
            this.deliveryCode = DeliveryCode.Delivery;
            this.promisedDate = null;
            this.patientAddressId = null;
        }
    }

    get wasShipped(): boolean {
        return this.status == OrderStatus.Shipped;
    }

    get isPromisedApplicable() : boolean {
        return isPromisedApplicable(this.status, this.promisedDate);
    }
}

export interface OrderOTCItem {
    orderId: number;
    otcItemId: number;
    quantity: number;
    otcItemName: string;
    otcItemUPC?: string;
    price: number;
    tax: number;
}

export interface OrderPrescription extends TrackedObject {
    orderId: number;
    storeID: number;
    rxNumber: number;
    rfNumber: number;
}
